import { useEffect, useState } from 'react';
import { useWebRTC } from '../context/WebRTCContext';
import { getFeaturesForView } from '../constants/viewFeatures';

/**
 * Hook to activate/deactivate features for a view
 * Automatically activates features on mount and deactivates on unmount
 *
 * @param {string} viewPath - View path (e.g., 'monitoring/sacred-timeline')
 */
export function useViewFeatures(viewPath) {
  const { activateFeatures, deactivateFeatures, featureManager, connectedPeers } = useWebRTC();
  // Depend on connectedPeers.length (a primitive, stable across renders) so the
  // effect re-fires when the data channel becomes available. Without this, a
  // view that mounts before the WebRTC peer connection is up sends its initial
  // request messages to zero open channels — they're silently dropped, and the
  // view sits in its loading skeleton forever.
  const peerCount = connectedPeers?.length ?? 0;

  useEffect(() => {
    const features = getFeaturesForView(viewPath);
    if (features.length === 0) {
      console.log(`[useViewFeatures] No features configured for view: ${viewPath}`);
      return;
    }

    console.log(`[useViewFeatures] Activating features for ${viewPath}:`, features, `(peers=${peerCount})`);
    activateFeatures(features);

    return () => {
      console.log(`[useViewFeatures] Deactivating features for ${viewPath}:`, features);
      deactivateFeatures(features);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewPath, activateFeatures, deactivateFeatures, peerCount]);

  return { featureManager };
}

/**
 * Hook to get a single feature instance by ID.
 *
 * Returns the feature, or `null` while we're still waiting for it to register.
 * Re-renders the caller once the feature shows up (so any `useEffect` with
 * `feature` in its deps will then fire and load data).
 *
 * Why this isn't just `featureManager.getFeature(id)`:
 *   `featureManager` is a long-lived ref on WebRTCContext. Feature registration
 *   happens asynchronously inside the context's mount effect and does NOT push
 *   a new context value to consumers — the manager object identity stays the
 *   same. So on a cold navigation, a view that mounts before registration
 *   finishes sees `getFeature(id)` return null and never gets re-notified. The
 *   old useMemo version of this hook had that exact bug. We now poll briefly
 *   on mount and `setFeature(f)` once the registry contains it.
 *
 * Polling stops on unmount, when the feature is found, or after a hard cap
 * so a genuinely-missing feature ID doesn't poll forever.
 *
 * @param {string} featureId - Feature ID (e.g., 'timeline')
 * @returns {Object|null} The feature instance or null
 */
export function useFeature(featureId) {
  const { featureManager } = useWebRTC();
  const [feature, setFeature] = useState(
    () => featureManager?.getFeature?.(featureId) ?? null
  );

  useEffect(() => {
    if (!featureManager) {
      setFeature(null);
      return undefined;
    }

    const tryFetch = () => {
      const f = featureManager.getFeature?.(featureId) ?? null;
      // Functional update so we only push a new value when it actually changes
      // (avoids resetting the React state on every poll tick when feature is null).
      setFeature(prev => (prev === f ? prev : f));
      return !!f;
    };

    if (tryFetch()) return undefined;

    const interval = setInterval(() => {
      if (tryFetch()) clearInterval(interval);
    }, 200);
    const cap = setTimeout(() => clearInterval(interval), 8000);

    return () => {
      clearInterval(interval);
      clearTimeout(cap);
    };
  }, [featureManager, featureId]);

  return feature;
}

/**
 * Hook to activate specific features (not tied to a view)
 *
 * @param {Array<string>} featureIds - Feature IDs to activate
 */
export function useFeatures(featureIds) {
  const { activateFeatures, deactivateFeatures, featureManager } = useWebRTC();

  useEffect(() => {
    if (!featureIds || featureIds.length === 0) return;

    console.log(`[useFeatures] Activating features:`, featureIds);
    activateFeatures(featureIds);

    return () => {
      console.log(`[useFeatures] Deactivating features:`, featureIds);
      deactivateFeatures(featureIds);
    };
  }, [featureIds, activateFeatures, deactivateFeatures]);

  return { featureManager };
}

/**
 * Convenience hook that activates view features AND returns a single feature instance.
 * Combines useViewFeatures + useFeature into one call.
 *
 * @param {string} viewPath - View path (e.g., 'punishment/phantom-cursor')
 * @param {string} featureId - Feature ID to retrieve (e.g., 'phantom-cursor')
 * @returns {Object|null} The feature instance or null
 */
export function useViewFeature(viewPath, featureId) {
  useViewFeatures(viewPath);
  return useFeature(featureId);
}
